import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Table2, KanbanSquare, Map as MapIcon, X, Globe, Link as LinkIcon, Plus, Trash2, Edit } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { apiGet, apiPost } from './api';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

export default function CRM() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [abaAtiva, setAbaAtiva] = useState('kanban'); 
  const [leads, setLeads] = useState([]);
  const [formConfig, setFormConfig] = useState(null); 
  const [carregando, setCarregando] = useState(true);
  const [leadSelecionado, setLeadSelecionado] = useState(null);

  const carregarDados = useCallback(async () => {
    try {
      const [dadosLeads, dadosForm] = await Promise.all([
        apiGet(`/get_leads.php?form_id=${id}`),
        apiGet(`/get_formulario.php?id=${id}`)
      ]);

      if (dadosLeads.sucesso) setLeads(dadosLeads.leads);
      if (dadosForm.sucesso) setFormConfig(dadosForm.form);
    } catch (e) { console.log("Erro ao buscar dados"); }
    finally { setCarregando(false); }
  }, [id]);

  useEffect(() => { carregarDados(); }, [carregarDados]);

  const estagios = formConfig?.estagios_crm || ['nao_iniciado', 'em_andamento', 'feito'];

  const formatarNomeEstagio = (estagio) => {
    if (estagio === 'nao_iniciado') return 'NÃO INICIADO';
    if (estagio === 'em_andamento') return 'EM ANDAMENTO';
    if (estagio === 'feito') return 'FEITO';
    return estagio.toUpperCase();
  };

  const adicionarEstagio = async () => {
    const nomeNovo = window.prompt("Qual o nome da nova etapa?");
    if (!nomeNovo || nomeNovo.trim() === '') return;

    const novosEstagios = [...estagios, nomeNovo];
    const novoFormConfig = { ...formConfig, estagios_crm: novosEstagios };
    setFormConfig(novoFormConfig);

    try {
      await apiPost('/salvar_formulario.php', { id: formConfig.id, titulo: formConfig.titulo, json_config: novoFormConfig });
    } catch (e) { alert("Erro ao salvar nova etapa"); }
  };

  const excluirEstagio = async (estagioExcluir) => {
    const leadsNestaEtapa = leads.filter(l => l.estagio_crm === estagioExcluir);
    if (leadsNestaEtapa.length > 0) {
      alert(`Você não pode excluir esta etapa, pois ela contém ${leadsNestaEtapa.length} lead(s).\nMova-os para outra coluna primeiro!`);
      return;
    }
    if (!window.confirm(`Tem certeza que deseja excluir a etapa "${formatarNomeEstagio(estagioExcluir)}"?`)) return;

    const novosEstagios = estagios.filter(e => e !== estagioExcluir);
    const novoFormConfig = { ...formConfig, estagios_crm: novosEstagios };
    setFormConfig(novoFormConfig);

    try {
      await apiPost('/salvar_formulario.php', { id: formConfig.id, titulo: formConfig.titulo, json_config: novoFormConfig });
    } catch (e) { alert("Erro ao excluir etapa"); }
  };

  const editarEstagio = async (estagioAntigo, index) => {
    const nomeNovo = window.prompt("Digite o novo nome para esta etapa:", formatarNomeEstagio(estagioAntigo));
    if (!nomeNovo || nomeNovo.trim() === '' || nomeNovo === formatarNomeEstagio(estagioAntigo)) return;

    const novosEstagios = [...estagios];
    novosEstagios[index] = nomeNovo;
    const novoFormConfig = { ...formConfig, estagios_crm: novosEstagios };
    
    // Atualiza a tela imediatamente
    setFormConfig(novoFormConfig);
    const novosLeads = leads.map(l => l.estagio_crm === estagioAntigo ? { ...l, estagio_crm: nomeNovo } : l);
    setLeads(novosLeads);

    try {
      // Atualiza o Form
      await apiPost('/salvar_formulario.php', { id: formConfig.id, titulo: formConfig.titulo, json_config: novoFormConfig });
      // Atualiza os Leads
      await apiPost('/renomear_estagio.php', { form_id: id, estagio_antigo: estagioAntigo, novo_estagio: nomeNovo });
    } catch (e) { alert("Erro ao renomear etapa"); }
  };

  const atualizarEstagioCRM = async (leadId, novoEstagio) => {
    const novosLeads = leads.map(l => l.id.toString() === leadId.toString() ? { ...l, estagio_crm: novoEstagio } : l);
    setLeads(novosLeads);
    if (leadSelecionado && leadSelecionado.id.toString() === leadId.toString()) setLeadSelecionado({ ...leadSelecionado, estagio_crm: novoEstagio });
    await apiPost('/atualizar_crm.php', { id: leadId, estagio_crm: novoEstagio });
  };

  const excluirLead = async (leadId) => {
    const confirmacao = window.prompt('Para excluir este lead definitivamente, digite a palavra EXCLUIR:');
    if (confirmacao !== 'EXCLUIR') { if (confirmacao !== null) alert("Palavra incorreta."); return; }
    try {
      await apiPost('/excluir_lead.php', { id: leadId });
      setLeadSelecionado(null); carregarDados();
    } catch (e) { alert("Erro ao excluir o lead."); }
  };

  // ======= ARRASTAR E SOLTAR (INTELIGENTE) =======
  const aoArrastarLead = (e, leadId) => {
    e.stopPropagation(); // Impede de arrastar a coluna junto
    e.dataTransfer.setData("leadId", leadId);
  };

  const aoArrastarColuna = (e, index) => {
    e.dataTransfer.setData("colunaIndex", index);
  };

  const aoSoltarNaColuna = async (e, indexAlvo, estagioAlvo) => {
    e.preventDefault();
    const leadId = e.dataTransfer.getData("leadId");
    const colIndexStr = e.dataTransfer.getData("colunaIndex");

    if (leadId) {
        atualizarEstagioCRM(leadId, estagioAlvo);
    } else if (colIndexStr) {
        const indexOrigem = parseInt(colIndexStr, 10);
        if (indexOrigem === indexAlvo) return;

        const novosEstagios = [...estagios];
        const itemArrastado = novosEstagios[indexOrigem];
        novosEstagios.splice(indexOrigem, 1);
        novosEstagios.splice(indexAlvo, 0, itemArrastado);

        const novoFormConfig = { ...formConfig, estagios_crm: novosEstagios };
        setFormConfig(novoFormConfig);

        try {
            await apiPost('/salvar_formulario.php', { id: formConfig.id, titulo: formConfig.titulo, json_config: novoFormConfig });
        } catch (e) { alert("Erro ao mover coluna"); }
    }
  };
  const permitirSoltar = (e) => e.preventDefault();

  const isTrafegoPago = (url) => url && (url.includes('utm_source') || url.includes('utm_campaign'));

  const getInfoLead = (respostas) => {
    if (!respostas) return { nome: null, empresa: null };
    const chaves = Object.keys(respostas);
    const chaveNome = chaves.find(k => k.toLowerCase().includes('nome') && !k.toLowerCase().includes('empresa') && !k.toLowerCase().includes('negócio'));
    const chaveEmpresa = chaves.find(k => k.toLowerCase().includes('empresa') || k.toLowerCase().includes('negócio'));
    return { nome: chaveNome ? respostas[chaveNome] : null, empresa: chaveEmpresa ? respostas[chaveEmpresa] : null };
  };

  const extrairDadosDeOrigem = (urlOriginal) => {
    if (!urlOriginal) return null;
    try {
      const url = new URL(urlOriginal);
      const params = url.searchParams;
      let fonte = params.get('utm_source');
      const campanha = params.get('utm_campaign');
      const anuncio = params.get('utm_content');
      let conjunto = params.get('utm_medium');
      if (!conjunto || conjunto.toLowerCase() === 'paid') {
          if (params.get('utm_term')) conjunto = params.get('utm_term');
      }
      if (fonte) {
          if (fonte.toLowerCase() === 'ig') fonte = 'Instagram';
          else if (fonte.toLowerCase() === 'fb') fonte = 'Facebook';
          else fonte = fonte.charAt(0).toUpperCase() + fonte.slice(1);
      } else {
          if (params.get('fbclid')) fonte = 'Facebook / Instagram';
          else if (params.get('gclid')) fonte = 'Google';
          else if (urlOriginal.includes('instagram.com')) fonte = 'Instagram';
      }
      return { fonte, campanha, conjunto, anuncio, raw: urlOriginal };
    } catch (e) { return { raw: urlOriginal }; }
  };

  const renderizarUTMs = (urlOriginal) => {
    const isPago = isTrafegoPago(urlOriginal);
    const badge = isPago ? <span style={{ display: 'inline-block', fontSize: '9px', background: '#10b981', color: '#fff', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold', marginBottom: '8px' }}>TRÁFEGO PAGO</span> : <span style={{ display: 'inline-block', fontSize: '9px', background: '#9ca3af', color: '#fff', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold', marginBottom: '8px' }}>ORGÂNICO</span>;
    if (!urlOriginal) return <div>{badge}<br/><span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Direto</span></div>;
    const dados = extrairDadosDeOrigem(urlOriginal);
    if (dados && (dados.fonte || dados.campanha || dados.conjunto || dados.anuncio)) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px' }}>
          {badge}
          {dados.fonte && <div><b style={{ color: '#6b7280' }}>Fonte:</b> {dados.fonte}</div>}
          {dados.campanha && <div><b style={{ color: '#6b7280' }}>Campanha:</b> {dados.campanha}</div>}
          {dados.conjunto && <div><b style={{ color: '#6b7280' }}>Conjunto:</b> {dados.conjunto}</div>}
          {dados.anuncio && <div><b style={{ color: '#6b7280' }}>Anúncio:</b> {dados.anuncio}</div>}
        </div>
      );
    }
    return <div>{badge}<br/><span style={{ color: '#9ca3af', fontStyle: 'italic', wordBreak: 'break-all' }}>{dados?.raw || 'Sem Link'}</span></div>;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: '#f9fafb', fontFamily: 'sans-serif' }}>
      <div style={{ padding: '20px', backgroundColor: '#fff', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', color: '#4b5563' }}><ArrowLeft size={16} /> Voltar</button>
          <h2 style={{ fontSize: '18px', margin: 0 }}>Resultados do Formulário</h2>
        </div>
        <div style={{ display: 'flex', gap: '10px', backgroundColor: '#f3f4f6', padding: '4px', borderRadius: '8px' }}>
          <button onClick={() => setAbaAtiva('kanban')} style={{ padding: '8px 16px', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 'bold', background: abaAtiva === 'kanban' ? '#fff' : 'transparent', color: abaAtiva === 'kanban' ? '#111827' : '#6b7280', boxShadow: abaAtiva === 'kanban' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}><KanbanSquare size={16} /> Kanban CRM</button>
          <button onClick={() => setAbaAtiva('tabela')} style={{ padding: '8px 16px', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 'bold', background: abaAtiva === 'tabela' ? '#fff' : 'transparent', color: abaAtiva === 'tabela' ? '#111827' : '#6b7280', boxShadow: abaAtiva === 'tabela' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}><Table2 size={16} /> Respostas</button>
          <button onClick={() => setAbaAtiva('mapa')} style={{ padding: '8px 16px', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 'bold', background: abaAtiva === 'mapa' ? '#fff' : 'transparent', color: abaAtiva === 'mapa' ? '#111827' : '#6b7280', boxShadow: abaAtiva === 'mapa' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}><MapIcon size={16} /> Mapa</button>
        </div>
      </div>

      <div style={{ flex: 1, padding: '20px', overflowY: 'auto' }}>
        {carregando ? <p>Carregando dados...</p> : (
          <>
            {abaAtiva === 'kanban' && (
              <div style={{ display: 'flex', gap: '20px', height: '100%', overflowX: 'auto', paddingBottom: '10px' }}>
                
                {estagios.map((estagio, index) => (
                  <div 
                    key={estagio} 
                    draggable
                    onDragStart={(e) => aoArrastarColuna(e, index)}
                    onDrop={(e) => aoSoltarNaColuna(e, index, estagio)} 
                    onDragOver={permitirSoltar} 
                    style={{ minWidth: '320px', flexShrink: 0, backgroundColor: '#f3f4f6', borderRadius: '8px', padding: '15px', display: 'flex', flexDirection: 'column', cursor: 'grab' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                      <h3 style={{ fontSize: '13px', color: '#6b7280', textTransform: 'uppercase', margin: 0 }}>{formatarNomeEstagio(estagio)}</h3>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <Edit size={14} color="#3b82f6" style={{ cursor: 'pointer', opacity: 0.6 }} onClick={() => editarEstagio(estagio, index)} title="Renomear" />
                        <Trash2 size={14} color="#ef4444" style={{ cursor: 'pointer', opacity: 0.6 }} onClick={() => excluirEstagio(estagio)} title="Excluir Coluna" />
                      </div>
                    </div>
                    
                    <div style={{ overflowY: 'auto', flex: 1 }}>
                      {leads.filter(l => l.estagio_crm === estagio).map(lead => {
                        const info = getInfoLead(lead.respostas);
                        const tituloLead = info.nome || `Lead #${lead.id}`;
                        
                        return (
                          <div key={lead.id} draggable onDragStart={(e) => aoArrastarLead(e, lead.id)} onClick={() => setLeadSelecionado(lead)} style={{ backgroundColor: '#fff', padding: '15px', borderRadius: '8px', marginBottom: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #e5e7eb', cursor: 'pointer' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                              <span style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 'bold' }}>#{lead.id}</span>
                              <span style={{ fontSize: '11px', color: '#9ca3af' }}>{new Date(lead.data_envio.replace(' ', 'T') + 'Z').toLocaleDateString('pt-BR')}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '5px' }}>
                              <p style={{ margin: 0, fontSize: '15px', fontWeight: 'bold', color: '#111827' }}>{tituloLead}</p>
                              {isTrafegoPago(lead.origem_url) ? <span style={{ fontSize: '9px', background: '#10b981', color: '#fff', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>PAGO</span> : <span style={{ fontSize: '9px', background: '#9ca3af', color: '#fff', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>ORGÂNICO</span>}
                            </div>
                            {info.empresa && <p style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#6b7280' }}>{info.empresa}</p>}
                            {!info.empresa && <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: '#9ca3af' }}>{lead.cidade}/{lead.estado}</p>}
                            <div style={{ marginTop: '10px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              <span style={{ padding: '3px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold', background: lead.status === 'completa' ? '#10b981' : '#fbbf24', color: '#fff' }}>{lead.status.toUpperCase()}</span>
                              {!!lead.qualificado && <span style={{ padding: '3px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold', background: '#7c3aed', color: '#fff' }}>⭐ QUALIFICADO</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}

                <div style={{ minWidth: '320px', flexShrink: 0 }}>
                   <button onClick={adicionarEstagio} style={{ width: '100%', padding: '15px', background: 'transparent', border: '2px dashed #d1d5db', borderRadius: '8px', color: '#6b7280', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyItems: 'center', gap: '8px', fontWeight: 'bold', fontSize: '14px', justifyContent: 'center' }}>
                     <Plus size={18} /> Adicionar Etapa
                   </button>
                </div>
              </div>
            )}

            {/* ABA TABELA */}
            {abaAtiva === 'tabela' && (
              <div style={{ background: '#fff', borderRadius: '8px', border: '1px solid #e5e7eb', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #e5e7eb', background: '#f9fafb', color: '#6b7280' }}>
                      <th style={{ padding: '12px 20px' }}>Data e Hora</th>
                      <th style={{ padding: '12px 20px' }}>Nome / Contato</th>
                      <th style={{ padding: '12px 20px' }}>Status</th>
                      <th style={{ padding: '12px 20px' }}>Origem da Captação</th>
                      <th style={{ padding: '12px 20px' }}>Respostas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map(lead => {
                      const info = getInfoLead(lead.respostas);
                      return (
                        <tr key={lead.id} style={{ borderBottom: '1px solid #e5e7eb', cursor: 'pointer' }} onClick={() => setLeadSelecionado(lead)}>
                          <td style={{ padding: '12px 20px' }}>{new Date(lead.data_envio.replace(' ', 'T') + 'Z').toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', dateStyle: 'short', timeStyle: 'short' })}</td>
                          <td style={{ padding: '12px 20px' }}>
                            <b style={{ color: '#111827' }}>{info.nome || `Lead #${lead.id}`}</b>
                            <br /><span style={{ fontSize: '12px', color: '#6b7280' }}>{info.empresa || lead.cidade}</span>
                          </td>
                          <td style={{ padding: '12px 20px' }}>
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              <span style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold', background: lead.status === 'completa' ? '#10b981' : '#fbbf24', color: '#fff' }}>{lead.status.toUpperCase()}</span>
                              {!!lead.qualificado && <span style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold', background: '#7c3aed', color: '#fff' }}>⭐ QUALIFICADO</span>}
                            </div>
                          </td>
                          <td style={{ padding: '12px 20px' }}>{renderizarUTMs(lead.origem_url)}</td>
                          <td style={{ padding: '12px 20px', color: '#3b82f6', fontSize: '12px', fontWeight: 'bold' }}>Ver detalhes...</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* ABA MAPA */}
            {abaAtiva === 'mapa' && (
              <div style={{ height: '70vh', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                <MapContainer center={[-14.2350, -51.9253]} zoom={4} style={{ height: '100%', width: '100%' }}>
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  {leads.filter(l => l.latitude && l.longitude).map(lead => {
                    const info = getInfoLead(lead.respostas);
                    return (
                      <Marker key={lead.id} position={[lead.latitude, lead.longitude]}>
                        <Popup><b style={{cursor: 'pointer', color: '#3b82f6'}} onClick={() => setLeadSelecionado(lead)}>{info.nome || `Lead #${lead.id}`}</b><br/>{info.empresa ? info.empresa : `${lead.cidade}/${lead.estado}`}</Popup>
                      </Marker>
                    );
                  })}
                </MapContainer>
              </div>
            )}
          </>
        )}
      </div>

      {/* ================= MODAL: DETALHES DO LEAD ================= */}
      {leadSelecionado && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, padding: '20px' }}>
          <div style={{ background: '#fff', width: '900px', maxWidth: '100%', height: '85vh', borderRadius: '8px', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
            <div style={{ padding: '20px 30px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '16px', color: '#111827', textTransform: 'uppercase', letterSpacing: '1px' }}>Detalhes da Resposta: #{leadSelecionado.id}</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                <button onClick={() => excluirLead(leadSelecionado.id)} style={{ background: '#fee2e2', color: '#ef4444', border: 'none', padding: '6px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}>Excluir Lead</button>
                <X size={24} color="#6b7280" style={{ cursor: 'pointer' }} onClick={() => setLeadSelecionado(null)} />
              </div>
            </div>
            
            <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
              <div style={{ flex: 2, padding: '30px', overflowY: 'auto', borderRight: '1px solid #e5e7eb' }}>
                <p style={{ fontSize: '12px', fontWeight: 'bold', color: '#9ca3af', marginBottom: '20px', textTransform: 'uppercase' }}>Campos da Resposta</p>
                {Object.entries(leadSelecionado.respostas || {}).map(([pergunta, resposta], index) => (
                  <div key={index} style={{ display: 'flex', gap: '15px', marginBottom: '30px' }}>
                    <div style={{ background: '#fcd34d', color: '#fff', borderRadius: '4px', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '13px', flexShrink: 0 }}>{index + 1}</div>
                    <div>
                      <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#4b5563' }}>{pergunta.replace(/&nbsp;/g, ' ')}</p>
                      <p style={{ margin: 0, fontSize: '15px', color: '#111827', fontWeight: '500' }}>{resposta}</p>
                    </div>
                  </div>
                ))}
                
                <div style={{ marginTop: '40px', paddingTop: '30px', borderTop: '1px solid #e5e7eb' }}>
                   <p style={{ fontSize: '12px', fontWeight: 'bold', color: '#9ca3af', marginBottom: '20px', textTransform: 'uppercase' }}>Dados Ocultos de Captação</p>
                   
                   <div style={{ display: 'flex', gap: '15px', marginBottom: '20px' }}>
                      <div style={{ background: '#e0e7ff', color: '#3b82f6', borderRadius: '4px', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Globe size={16} /></div>
                      <div>
                        <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#6b7280' }}>Localização e IP</p>
                        <p style={{ margin: 0, fontSize: '14px', color: '#111827', fontWeight: '500' }}>{leadSelecionado.cidade} - {leadSelecionado.estado} ({leadSelecionado.ip})</p>
                      </div>
                   </div>

                   <div style={{ display: 'flex', gap: '15px', marginBottom: '20px' }}>
                      <div style={{ background: '#e0e7ff', color: '#3b82f6', borderRadius: '4px', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><LinkIcon size={16} /></div>
                      <div style={{ overflow: 'hidden', width: '100%' }}>
                        <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#6b7280', textTransform: 'uppercase', fontWeight: 'bold' }}>Dados de Origem</p>
                        
                        {(() => {
                            const dadosOrigem = extrairDadosDeOrigem(leadSelecionado.origem_url);
                            if (!dadosOrigem || (!dadosOrigem.fonte && !dadosOrigem.campanha && !dadosOrigem.conjunto && !dadosOrigem.anuncio)) {
                                return <p style={{ margin: 0, fontSize: '14px', color: '#111827' }}>Acesso Direto / Orgânico</p>;
                            }
                            
                            return (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    {dadosOrigem.fonte && <p style={{ margin: 0, fontSize: '14px', color: '#111827' }}><b style={{ color: '#6b7280' }}>Fonte:</b> {dadosOrigem.fonte}</p>}
                                    {dadosOrigem.campanha && <p style={{ margin: 0, fontSize: '14px', color: '#111827' }}><b style={{ color: '#6b7280' }}>Campanha:</b> {dadosOrigem.campanha}</p>}
                                    {dadosOrigem.conjunto && <p style={{ margin: 0, fontSize: '14px', color: '#111827' }}><b style={{ color: '#6b7280' }}>Conjunto:</b> {dadosOrigem.conjunto}</p>}
                                    {dadosOrigem.anuncio && <p style={{ margin: 0, fontSize: '14px', color: '#111827' }}><b style={{ color: '#6b7280' }}>Anúncio:</b> {dadosOrigem.anuncio}</p>}
                                    
                                    <details style={{ marginTop: '8px' }}>
                                        <summary style={{ fontSize: '11px', color: '#3b82f6', cursor: 'pointer', outline: 'none' }}>Ver Link Completo</summary>
                                        <p style={{ margin: '5px 0 0 0', fontSize: '11px', color: '#9ca3af', wordBreak: 'break-all' }}>{dadosOrigem.raw}</p>
                                    </details>
                                </div>
                            );
                        })()}
                      </div>
                   </div>

                </div>
              </div>

              <div style={{ flex: 1, padding: '30px', backgroundColor: '#f9fafb', overflowY: 'auto' }}>
                 <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px' }}>
                    <p style={{ fontSize: '12px', fontWeight: 'bold', color: '#9ca3af', marginBottom: '20px', textTransform: 'uppercase' }}>Propriedades do CRM</p>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                      <span style={{ color: '#4b5563', fontSize: '13px' }}>Status</span>
                      <span style={{ background: leadSelecionado.status === 'completa' ? '#10b981' : '#fbbf24', color: '#fff', padding: '4px 10px', borderRadius: '4px', fontWeight: 'bold', fontSize: '11px', letterSpacing: '0.5px' }}>
                        {leadSelecionado.status.toUpperCase()}
                      </span>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                      <span style={{ color: '#4b5563', fontSize: '13px' }}>Qualificado</span>
                      {leadSelecionado.qualificado
                        ? <span style={{ background: '#7c3aed', color: '#fff', padding: '4px 10px', borderRadius: '4px', fontWeight: 'bold', fontSize: '11px', letterSpacing: '0.5px' }}>⭐ SIM</span>
                        : <span style={{ color: '#9ca3af', fontSize: '13px' }}>Não</span>}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                      <span style={{ color: '#4b5563', fontSize: '13px' }}>Estágio</span>
                      <select 
                        value={leadSelecionado.estagio_crm} 
                        onChange={(e) => atualizarEstagioCRM(leadSelecionado.id, e.target.value)}
                        style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', outline: 'none', backgroundColor: '#f9fafb', fontWeight: 'bold', color: '#111827', cursor: 'pointer' }}
                      >
                        {estagios.map(e => <option key={e} value={e}>{formatarNomeEstagio(e)}</option>)}
                      </select>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '15px', borderTop: '1px solid #e5e7eb' }}>
                      <span style={{ color: '#4b5563', fontSize: '13px' }}>Recebido em</span>
                      <span style={{ color: '#111827', fontSize: '13px', fontWeight: 'bold' }}>
                        {new Date(leadSelecionado.data_envio.replace(' ', 'T') + 'Z').toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', dateStyle: 'long', timeStyle: 'medium' })}
                      </span>
                    </div>
                 </div>
              </div>

            </div>
          </div>
        </div>
      )}

    </div>
  );
}